import { supabase } from '../supabaseClient';
import type { LandingPageSection, LandingPageSectionUpdate } from '../types/landingPage';
import { IMAGE_WEBP_MIME, DEFAULT_WEBP_QUALITY } from '../utils/imageCompress';
import { getAllIntroSections } from './landingIntroService';
import {
  addLandingStoragePath,
  collectLandingStoragePathsFromUnknown,
  diffOrphanLandingStorageNames,
  LANDING_PAGE_IMAGES_BUCKET,
} from '../utils/landingStoragePaths';

const LANDING_SECTIONS_CACHE_KEY = 'curriculum-landing-sections.v11';
const CACHE_VERSION_KEY = 'curriculum-landing-cache-version';

interface CachedSections {
  data: LandingPageSection[];
  /** 행 메타 지문 — head 비교용 */
  version: string;
  lastUpdated: string;
}

interface LandingSectionVersionRow {
  id: string;
  section_key: string;
  section_order: number;
  is_active: boolean;
  updated_at: string;
  image_version: number;
}

const FEATURE_KEY_RE = /^feature\d+$/;
const FEATURE_SLIDE_KEY_RE = /^feature\d+_slide\d+$/;

export function isLandingFeatureKey(key: string): boolean {
  return FEATURE_KEY_RE.test(key);
}

export function isLandingFeatureSlideKey(key: string): boolean {
  return FEATURE_SLIDE_KEY_RE.test(key);
}

export function featureSlideKeys(featureKey: string): string[] {
  return [1, 2, 3].map((n) => `${featureKey}_slide${n}`);
}

function maxUpdatedAt(rows: { updated_at: string }[]): string {
  return rows.reduce(
    (max, r) => (r.updated_at > max ? r.updated_at : max),
    rows[0]?.updated_at ?? '',
  );
}

/** 신선도 메타만으로 버전 문자열 생성 (표시 문구 제외) */
export function buildLandingSectionsCacheVersion(rows: LandingSectionVersionRow[]): string {
  return [...rows]
    .sort((a, b) => a.section_key.localeCompare(b.section_key))
    .map(
      (r) =>
        `${r.section_key}:${r.section_order}:${r.is_active ? 1 : 0}:${r.image_version}:${r.updated_at}`,
    )
    .join('|');
}

/**
 * 가벼운 head 조회 — 본문·이미지 URL 없이 버전 메타만
 */
export async function fetchLandingSectionsCacheVersion(): Promise<string | null> {
  const { data, error } = await supabase
    .from('landing_page_content')
    .select('id, section_key, section_order, is_active, updated_at, image_version');

  if (error) throw error;
  return buildLandingSectionsCacheVersion((data ?? []) as LandingSectionVersionRow[]);
}

/**
 * 모든 활성화된 섹션을 section_order 순으로 조회 (공개 랜딩용, 풀 fetch)
 */
export async function getSections(): Promise<LandingPageSection[]> {
  const { data, error } = await supabase
    .from('landing_page_content')
    .select('*')
    .eq('is_active', true)
    .order('section_order', { ascending: true });

  if (error) throw error;
  return (data ?? []) as LandingPageSection[];
}

/**
 * 공개 랜딩: LS 캐시 즉시 재사용 + head 버전 비교 후 변경 시에만 풀 조회
 */
export async function getSectionsWithCache(options?: {
  forceRefresh?: boolean;
}): Promise<{ sections: LandingPageSection[]; fromCache: boolean }> {
  const cached = loadCachedSections();

  if (!options?.forceRefresh && cached?.version && cached.data?.length) {
    try {
      const remoteVersion = await fetchLandingSectionsCacheVersion();
      if (remoteVersion != null && remoteVersion === cached.version) {
        return {
          sections: cached.data
            .filter((s) => s.is_active)
            .sort((a, b) => a.section_order - b.section_order),
          fromCache: true,
        };
      }
    } catch {
      /* head 실패 시 풀 조회로 진행 */
    }
  }

  const { data, error } = await supabase
    .from('landing_page_content')
    .select('*')
    .order('section_order', { ascending: true });

  if (error) throw error;
  const all = (data ?? []) as LandingPageSection[];
  const version = buildLandingSectionsCacheVersion(
    all.map((s) => ({
      id: s.id,
      section_key: s.section_key,
      section_order: s.section_order,
      is_active: s.is_active,
      updated_at: s.updated_at,
      image_version: s.image_version ?? 1,
    })),
  );
  saveSectionsCache(all, version);
  return {
    sections: all
      .filter((s) => s.is_active)
      .sort((a, b) => a.section_order - b.section_order),
    fromCache: false,
  };
}

export function isLandingPartnerLogoKey(key: string): boolean {
  return /^partner\d+$/.test(key);
}

/**
 * 협업기관 부모 섹션이 없으면 생성.
 * 로고(partnerN)는 관리자가 추가·삭제하므로 고정 슬롯을 다시 심지 않음.
 * 로고가 하나도 없을 때만 partner1 하나를 보장한다.
 */
export async function ensurePartnerSections(): Promise<void> {
  const { data: existing, error: selectError } = await supabase
    .from('landing_page_content')
    .select('section_key, section_order');

  if (selectError) throw selectError;

  const rows = (existing ?? []) as Array<{ section_key: string; section_order: number }>;
  const keys = new Set(rows.map((row) => row.section_key));
  const missing: Array<{
    section_key: string;
    section_order: number;
    title: string;
    subtitle: string | null;
    body: null;
    cta_label: null;
    cta_link: null;
    is_active: boolean;
  }> = [];

  if (!keys.has('partners')) {
    missing.push({
      section_key: 'partners',
      section_order: 15,
      title: '협업기관',
      subtitle: '함께하는 기관·학교',
      body: null,
      cta_label: null,
      cta_link: null,
      is_active: true,
    });
  }

  const hasLogo = rows.some((row) => isLandingPartnerLogoKey(row.section_key));
  if (!hasLogo) {
    const partnersOrder =
      rows.find((row) => row.section_key === 'partners')?.section_order ?? 15;
    missing.push({
      section_key: 'partner1',
      section_order: partnersOrder + 1,
      title: '협업기관 1',
      subtitle: null,
      body: null,
      cta_label: null,
      cta_link: null,
      is_active: true,
    });
  }

  if (missing.length === 0) return;

  const { error: insertError } = await supabase.from('landing_page_content').insert(missing);
  if (insertError) throw insertError;
  clearSectionsCache();
}

/**
 * FEATURE별 이미지 슬라이드(featureN_slide1~3)가 없으면 생성 (관리자 CMS용, 멱등)
 */
export async function ensureFeatureSlideSections(): Promise<void> {
  const { data: existing, error: selectError } = await supabase
    .from('landing_page_content')
    .select('section_key, section_order, title, image_path, image_url, image_version');

  if (selectError) throw selectError;

  const rows = (existing ?? []) as Array<{
    section_key: string;
    section_order: number;
    title: string;
    image_path: string | null;
    image_url: string | null;
    image_version: number;
  }>;
  const keys = new Set(rows.map((row) => row.section_key));
  const features = rows.filter((row) => isLandingFeatureKey(row.section_key));

  const missing: Array<{
    section_key: string;
    section_order: number;
    title: string;
    subtitle: null;
    body: null;
    cta_label: null;
    cta_link: null;
    is_active: boolean;
  }> = [];

  for (const feature of features) {
    for (let i = 1; i <= 3; i += 1) {
      const slideKey = `${feature.section_key}_slide${i}`;
      if (keys.has(slideKey)) continue;
      missing.push({
        section_key: slideKey,
        section_order: feature.section_order * 10 + i,
        title: `${feature.title || feature.section_key} 이미지 ${i}`,
        subtitle: null,
        body: null,
        cta_label: null,
        cta_link: null,
        is_active: true,
      });
    }
  }

  if (missing.length > 0) {
    const { error: insertError } = await supabase.from('landing_page_content').insert(missing);
    if (insertError) throw insertError;
  }

  // 기존 feature 단일 이미지 → slide1 이전 (slide1이 비어 있을 때만)
  for (const feature of features) {
    if (!feature.image_url) continue;
    const slide1Key = `${feature.section_key}_slide1`;
    const slide1 = rows.find((row) => row.section_key === slide1Key);
    const slide1Empty = !slide1?.image_url;
    const slide1JustCreated = missing.some((row) => row.section_key === slide1Key);
    if (!slide1Empty && !slide1JustCreated) continue;

    const { error: moveError } = await supabase
      .from('landing_page_content')
      .update({
        image_path: feature.image_path,
        image_url: feature.image_url,
        image_version: feature.image_version || 1,
        updated_at: new Date().toISOString(),
      })
      .eq('section_key', slide1Key);
    if (moveError) throw moveError;

    const { error: clearError } = await supabase
      .from('landing_page_content')
      .update({
        image_path: null,
        image_url: null,
        updated_at: new Date().toISOString(),
      })
      .eq('section_key', feature.section_key);
    if (clearError) throw clearError;
  }

  if (missing.length > 0 || features.some((f) => f.image_url)) {
    clearSectionsCache();
  }
}

/**
 * 활성/비활성 포함 전체 섹션 (관리자 CMS용)
 */
export async function getAllSections(): Promise<LandingPageSection[]> {
  const { data, error } = await supabase
    .from('landing_page_content')
    .select('*')
    .order('section_order', { ascending: true });

  if (error) throw error;
  return (data ?? []) as LandingPageSection[];
}

/**
 * 특정 섹션 조회
 */
export async function getSection(sectionKey: string): Promise<LandingPageSection | null> {
  const { data, error } = await supabase
    .from('landing_page_content')
    .select('*')
    .eq('section_key', sectionKey)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }
  return data as LandingPageSection;
}

/**
 * 섹션 업데이트 (텍스트, 이미지 URL, 버전)
 */
export async function updateSection(
  sectionKey: string,
  updates: LandingPageSectionUpdate
): Promise<LandingPageSection> {
  const { data, error } = await supabase
    .from('landing_page_content')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('section_key', sectionKey)
    .select()
    .single();

  if (error) throw error;

  // 캐시 무효화
  clearSectionsCache();

  return data as LandingPageSection;
}

/**
 * 이미지를 Storage에 업로드하고 Public URL 반환
 */
export async function uploadSectionImage(
  sectionKey: string,
  file: File
): Promise<{ path: string; url: string }> {
  // 이미 크롭·WebP로 만든 파일은 재인코딩하지 않음
  const webpBlob =
    file.type === IMAGE_WEBP_MIME ? file : await compressToWebP(file);

  // 파일명 생성: section_key_timestamp.webp
  const timestamp = Date.now();
  const fileName = `${sectionKey}_${timestamp}.webp`;
  const filePath = fileName;

  // Storage에 업로드
  const { error: uploadError } = await supabase.storage
    .from('landing-page-images')
    .upload(filePath, webpBlob, {
      contentType: IMAGE_WEBP_MIME,
      cacheControl: '31536000', // 1년
      upsert: false,
    });

  if (uploadError) {
    const msg = String(uploadError.message || '');
    if (/bucket not found/i.test(msg)) {
      throw new Error(
        'Storage 버킷 landing-page-images 가 없습니다. Supabase SQL Editor에서 마이그레이션 20260730131000_landing_page_images_storage.sql 을 실행해 주세요.',
      );
    }
    throw uploadError;
  }

  // Public URL 가져오기
  const { data: urlData } = supabase.storage
    .from('landing-page-images')
    .getPublicUrl(filePath);

  return {
    path: filePath,
    url: urlData.publicUrl,
  };
}

/**
 * Storage에서 이미지 삭제
 */
export async function deleteSectionImage(imagePath: string): Promise<void> {
  if (!imagePath) return;

  const { error } = await supabase.storage
    .from('landing-page-images')
    .remove([imagePath]);

  if (error) throw error;
}

/**
 * 협업기관 로고 행 추가
 */
export async function createPartnerSection(input: {
  section_key: string;
  section_order: number;
  title: string;
  cta_link?: string | null;
  is_active?: boolean;
}): Promise<LandingPageSection> {
  const { data, error } = await supabase
    .from('landing_page_content')
    .insert({
      section_key: input.section_key,
      section_order: input.section_order,
      title: input.title,
      subtitle: null,
      body: null,
      cta_label: null,
      cta_link: input.cta_link?.trim() || null,
      is_active: input.is_active !== false,
    })
    .select()
    .single();

  if (error) throw error;
  clearSectionsCache();
  return data as LandingPageSection;
}

/**
 * 협업기관 로고 행 삭제 (Storage 이미지도 정리)
 */
export async function deletePartnerSection(
  sectionKey: string,
  imagePath?: string | null,
): Promise<void> {
  if (!isLandingPartnerLogoKey(sectionKey)) {
    throw new Error(`Invalid partner section key: ${sectionKey}`);
  }

  if (imagePath) {
    try {
      await deleteSectionImage(imagePath);
    } catch (err) {
      console.warn('협업기관 이미지 삭제 실패:', err);
    }
  }

  const { error } = await supabase
    .from('landing_page_content')
    .delete()
    .eq('section_key', sectionKey);

  if (error) throw error;
  clearSectionsCache();
}

/**
 * 이미지를 WebP로 압축
 */
async function compressToWebP(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const img = new Image();
        img.onload = async () => {
          const canvas = document.createElement('canvas');
          
          // 최대 너비/높이 설정 (랜딩 페이지용으로 1920px로 제한)
          const MAX_WIDTH = 1920;
          const MAX_HEIGHT = 1920;
          
          let { width, height } = img;
          
          if (width > MAX_WIDTH || height > MAX_HEIGHT) {
            const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
            width = Math.floor(width * ratio);
            height = Math.floor(height * ratio);
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas context 생성 실패'));
            return;
          }
          
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('WebP 변환 실패'));
              }
            },
            IMAGE_WEBP_MIME,
            DEFAULT_WEBP_QUALITY
          );
        };
        img.onerror = () => reject(new Error('이미지 로드 실패'));
        img.src = e.target?.result as string;
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('파일 읽기 실패'));
    reader.readAsDataURL(file);
  });
}

/**
 * localStorage에서 섹션 데이터 로드
 */
export function loadCachedSections(): CachedSections | null {
  try {
    const cached = localStorage.getItem(LANDING_SECTIONS_CACHE_KEY);
    if (!cached) return null;
    const parsed = JSON.parse(cached) as CachedSections;
    if (!parsed?.version || !Array.isArray(parsed.data)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * localStorage에 섹션 데이터 저장
 */
export function saveSectionsCache(sections: LandingPageSection[], version?: string): void {
  try {
    const resolvedVersion =
      version ??
      buildLandingSectionsCacheVersion(
        sections.map((s) => ({
          id: s.id,
          section_key: s.section_key,
          section_order: s.section_order,
          is_active: s.is_active,
          updated_at: s.updated_at,
          image_version: s.image_version ?? 1,
        })),
      );

    const cached: CachedSections = {
      data: sections,
      version: resolvedVersion,
      lastUpdated: maxUpdatedAt(sections) || new Date().toISOString(),
    };
    localStorage.setItem(LANDING_SECTIONS_CACHE_KEY, JSON.stringify(cached));
  } catch {
    // localStorage 쓰기 실패 무시
  }
}

/**
 * localStorage 캐시 무효화
 */
export function clearSectionsCache(): void {
  try {
    localStorage.removeItem(LANDING_SECTIONS_CACHE_KEY);
    localStorage.removeItem('curriculum-landing-sections.v10');
    localStorage.removeItem('curriculum-landing-sections.v9');
    localStorage.removeItem('curriculum-landing-sections.v8');
    localStorage.removeItem('curriculum-landing-sections.v7');
    localStorage.removeItem('curriculum-landing-sections.v6');
    localStorage.setItem(CACHE_VERSION_KEY, Date.now().toString());
  } catch {
    // localStorage 쓰기 실패 무시
  }
}

async function listLandingStorageObjectNames(): Promise<string[]> {
  const names: string[] = [];
  const limit = 100;
  for (let offset = 0; offset < 5000; offset += limit) {
    const { data, error } = await supabase.storage.from(LANDING_PAGE_IMAGES_BUCKET).list('', {
      limit,
      offset,
      sortBy: { column: 'name', order: 'asc' },
    });
    if (error) throw error;
    const batch = data ?? [];
    for (const item of batch) {
      if (!item.name || item.name.startsWith('.')) continue;
      if (item.id == null && item.metadata == null) continue;
      names.push(item.name);
    }
    if (batch.length < limit) break;
  }
  return names;
}

export async function collectReferencedLandingStoragePaths(): Promise<Set<string>> {
  const out = new Set<string>();
  const sections = await getAllSections();
  for (const section of sections) {
    addLandingStoragePath(out, section.image_path, section.image_url);
  }
  const intros = await getAllIntroSections();
  for (const intro of intros) {
    collectLandingStoragePathsFromUnknown(intro.content_json, out);
    collectLandingStoragePathsFromUnknown(intro.layout_json, out);
  }
  return out;
}

/** DB에 더 이상 안 쓰이는 landing-page-images 객체를 지운다. */
export async function purgeOrphanLandingStorageImages(): Promise<{
  deleted: string[];
  kept: number;
}> {
  const [referenced, stored] = await Promise.all([
    collectReferencedLandingStoragePaths(),
    listLandingStorageObjectNames(),
  ]);
  const orphans = diffOrphanLandingStorageNames(stored, referenced);
  if (!orphans.length) return { deleted: [], kept: stored.length };

  const deleted: string[] = [];
  for (let i = 0; i < orphans.length; i += 50) {
    const chunk = orphans.slice(i, i + 50);
    const { error } = await supabase.storage.from(LANDING_PAGE_IMAGES_BUCKET).remove(chunk);
    if (error) throw error;
    deleted.push(...chunk);
  }
  return { deleted, kept: stored.length - deleted.length };
}
